// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — UI Primitives
// Card, Button, Input, Badge, Toggle, Select
// ══════════════════════════════════════════════════════════════════════════════

import { useTheme } from '@contexts/ThemeContext';

// ─── Card ─────────────────────────────────────────────────────────────────
export function Card({ children, style = {}, onClick, hoverable = false, ...rest }) {
  const { theme } = useTheme();
  return (
    <div
      onClick={onClick}
      style={{
        background: theme.bgCard,
        borderRadius: 14,
        border: `1px solid ${theme.border}`,
        boxShadow: theme.shadow,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s, box-shadow 0.15s',
        ...style
      }}
      onMouseEnter={e => {
        if (hoverable || onClick) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = theme.shadowElevated;
        }
      }}
      onMouseLeave={e => {
        if (hoverable || onClick) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = theme.shadow;
        }
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

// ─── Button ────────────────────────────────────────────────────────────────
export function Button({
  children, onClick, variant = 'primary', size = 'md',
  disabled = false, style = {}, type = 'button', fullWidth = false, ...rest
}) {
  const { theme } = useTheme();
  const variants = {
    primary: { background: theme.primary, color: '#fff' },
    ghost: { background: 'transparent', color: theme.textSecondary, border: `1px solid ${theme.border}` },
    outline: { background: 'transparent', color: theme.primary, border: `1px solid ${theme.primaryBorder}` },
    danger: { background: theme.danger, color: '#fff' },
    success: { background: theme.success, color: '#fff' }
  };
  const sizes = {
    sm: { padding: '6px 12px', fontSize: 12 },
    md: { padding: '10px 18px', fontSize: 13 },
    lg: { padding: '12px 22px', fontSize: 14 }
  };

  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        border: 'none',
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        opacity: disabled ? 0.5 : 1,
        fontFamily: 'inherit',
        transition: 'opacity 0.15s, transform 0.1s',
        width: fullWidth ? '100%' : 'auto',
        ...sizes[size],
        ...variants[variant],
        ...style
      }}
      onMouseDown={e => !disabled && (e.currentTarget.style.transform = 'scale(0.97)')}
      onMouseUp={e => !disabled && (e.currentTarget.style.transform = 'scale(1)')}
      onMouseLeave={e => !disabled && (e.currentTarget.style.transform = 'scale(1)')}
      {...rest}
    >
      {children}
    </button>
  );
}

// ─── Input ─────────────────────────────────────────────────────────────────
export function Input({ value, onChange, placeholder, type = 'text', style = {}, disabled = false, ...rest }) {
  const { theme } = useTheme();
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '10px 14px',
        borderRadius: 8,
        border: `1px solid ${theme.border}`,
        background: theme.bgInput,
        color: theme.text,
        fontSize: 14,
        outline: 'none',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        transition: 'border-color 0.15s',
        ...style
      }}
      onFocus={e => (e.currentTarget.style.borderColor = theme.primary)}
      onBlur={e => (e.currentTarget.style.borderColor = theme.border)}
      {...rest}
    />
  );
}

// ─── Textarea ──────────────────────────────────────────────────────────────
export function Textarea({ value, onChange, placeholder, rows = 4, style = {}, ...rest }) {
  const { theme } = useTheme();
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%',
        padding: '10px 14px',
        borderRadius: 8,
        border: `1px solid ${theme.border}`,
        background: theme.bgInput,
        color: theme.text,
        fontSize: 14,
        outline: 'none',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        resize: 'vertical',
        minHeight: 80,
        ...style
      }}
      onFocus={e => (e.currentTarget.style.borderColor = theme.primary)}
      onBlur={e => (e.currentTarget.style.borderColor = theme.border)}
      {...rest}
    />
  );
}

// ─── Select ────────────────────────────────────────────────────────────────
export function Select({ value, onChange, options = [], style = {}, ...rest }) {
  const { theme } = useTheme();
  return (
    <select
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: '10px 14px',
        borderRadius: 8,
        border: `1px solid ${theme.border}`,
        background: theme.bgInput,
        color: theme.text,
        fontSize: 14,
        outline: 'none',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        cursor: 'pointer',
        ...style
      }}
      {...rest}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ─── Badge ─────────────────────────────────────────────────────────────────
export function Badge({ children, variant = 'primary' }) {
  const { theme } = useTheme();
  const variants = {
    primary: [theme.primary, theme.primaryDim],
    danger: [theme.danger, theme.dangerDim],
    warning: [theme.warning, theme.warningDim],
    success: [theme.success, theme.successDim],
    info: [theme.info, theme.infoDim],
    neutral: [theme.textSecondary, theme.bgInput]
  };
  const [fg, bg] = variants[variant] || variants.primary;
  return (
    <span style={{
      color: fg,
      background: bg,
      padding: '3px 10px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4
    }}>
      {children}
    </span>
  );
}

// ─── Toggle ────────────────────────────────────────────────────────────────
export function Toggle({ checked, onChange, label, disabled = false }) {
  const { theme } = useTheme();
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
      onClick={disabled ? undefined : () => onChange(!checked)}
    >
      <div style={{
        width: 42, height: 24, borderRadius: 12,
        background: checked ? theme.primary : theme.bgInput,
        border: `1px solid ${checked ? theme.primary : theme.border}`,
        position: 'relative',
        transition: 'all 0.2s'
      }}>
        <div style={{
          width: 18, height: 18, borderRadius: '50%',
          background: '#fff',
          position: 'absolute',
          top: 2,
          left: checked ? 21 : 3,
          transition: 'all 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
        }} />
      </div>
      {label && <span style={{ fontSize: 13, color: theme.text }}>{label}</span>}
    </div>
  );
}

// ─── Grid (responsive) ─────────────────────────────────────────────────────
export function Grid({ children, min = 220, gap = 16, style = {} }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`,
      gap,
      ...style
    }}>
      {children}
    </div>
  );
}
